/*
 * Common detail-header builder — module-centric and self-completing.
 *
 * Every file under ./modules/*.js is auto-imported and registered by its
 * `module` key. Adding a brand-new module is optional: if a config exists its
 * `buildData` is used, otherwise a GENERIC builder derives a sensible header
 * from common field-name patterns. Either way every module page gets a header.
 *
 * Each module config may expose:
 *   module                         → key matched against `moduleName`
 *   tabs → static navigation UI. Actions are loaded from Admin configuration.
 *   buildData({ val, has })        → dynamic header parts from the API fields
 *
 * Data parts (eyebrow / title / status / ids / meta / stats / timestamp) come
 * from buildData, with generic fallbacks merged in for anything it omits.
 * Icons are kept as string keys and mapped to components in DetailHeaderCard.
 */
import {
  asText,
  capitalize,
  formatDateTime,
  has,
  isEmpty,
  statusColor,
} from './configUtils';
import { buildFromSpec } from './headerSpec';

// Vite glob — eagerly pulls in every module config at build time.
const moduleFiles = import.meta.glob('./modules/*.js', { eager: true });

const REGISTRY = {};
Object.values(moduleFiles).forEach((mod) => {
  const config = mod.default ?? Object.values(mod)[0];
  if (config?.module) REGISTRY[config.module] = config;
});

// Singular/plural + alias map so 'jobs'/'job', 'candidates'/'candidate',
// 'submissions'/'submission', etc. all resolve to the registered config key.
const MODULE_ALIASES = {
  job: 'job', jobs: 'job',
  candidate: 'candidate', candidates: 'candidate', applicant: 'candidate', applicants: 'candidate',
  submission: 'submission', submissions: 'submission',
  vendor: 'vendor', vendors: 'vendor',
  onboarding: 'onboarding',
};

// Returns the effective registry: built-in glob-discovered configs merged with
// whatever the host injected via configureCoreTheme({ detailConfig: { … } }).
// Called at render time so host configs registered in main.jsx are always seen.
function getEffectiveRegistry() {
  const hostConfigs =
    (typeof window !== 'undefined' && window.__CORE_THEME_CONFIG__?.detailConfig) ?? {};
  if (!Object.keys(hostConfigs).length) return REGISTRY;
  return { ...REGISTRY, ...hostConfigs };
}

// Resolve whatever name a page passes to a registered config key (best-effort).
function resolveModuleKey(moduleName = '', registry = REGISTRY) {
  const raw = String(moduleName).trim();
  if (registry[raw]) return raw;

  const lower = raw.toLowerCase();
  if (registry[lower]) return lower;

  const alias = MODULE_ALIASES[lower];
  if (alias && registry[alias]) return alias;

  // Last-ditch singular/plural flip.
  const flipped = lower.endsWith('s') ? lower.slice(0, -1) : `${lower}s`;
  if (registry[flipped]) return flipped;

  return alias ?? lower;
}

// Flatten every field across groups into a key → field lookup.
function indexFields(formGroups = []) {
  const map = new Map();
  formGroups.forEach((group) => {
    (group.fields ?? []).forEach((field) => {
      if (!map.has(field.field)) map.set(field.field, field);
    });
  });
  return map;
}

// Returns the first non-empty value across a list of candidate keys. A key may
// be a "a+b" pair, which joins the two field values (e.g. firstName+lastName).
function pickFirst(val, keys) {
  for (const key of keys) {
    if (key.includes('+')) {
      const joined = key.split('+').map((k) => val(k)).filter(has).map(asText).join(' ');
      if (joined) return joined;
    } else if (has(val(key))) {
      return val(key);
    }
  }
  return undefined;
}

/*
 * Generic, module-agnostic header. Works for ANY module by probing the most
 * common field names. Per-module configs override these where they care.
 */
function genericBuildData({ val }) {
  const title = pickFirst(val, [
    'jobTitle', 'firstName+lastName', 'name', 'fullName', 'title',
    'clientName', 'vendorCompanyName', 'companyName',
  ]) || '-';

  const eyebrowRaw = pickFirst(val, [
    'clientName', 'source', 'designation', 'jobReferenceId', 'applicantRefrenceId',
  ]);
  const eyebrow = has(eyebrowRaw) ? asText(eyebrowRaw) : undefined;

  const statusRaw = pickFirst(val, ['status', 'jobStatus', 'submissionStatus', 'stage']);
  const status = has(statusRaw)
    ? { label: capitalize(statusRaw), color: statusColor(statusRaw) }
    : null;

  // Reference-id chips under the title.
  const ids = [];
  const idChips = [
    ['Reference ID', ['jobReferenceId', 'applicantRefrenceId', 'submissionId', 'referenceId', 'refId']],
    ['Client Req ID', ['clientJobReferenceId', 'jobClientRefrenceId', 'clientReqId']],
    ['MS Req ID', ['mspreqId', 'mspReqId']],
  ];
  idChips.forEach(([label, keys]) => {
    const v = pickFirst(val, keys);
    if (has(v)) ids.push({ label, value: asText(v) });
  });

  // A small meta row.
  const meta = [];
  const location = pickFirst(val, ['jobLocation', 'currentLocation', 'location']);
  if (has(location)) meta.push({ icon: 'location', text: asText(location) });
  const designation = pickFirst(val, ['designation']);
  if (has(designation)) meta.push({ icon: 'designation', text: asText(designation) });

  const created = formatDateTime(pickFirst(val, ['createdAt', 'submittedAt', 'createdDate']));
  const timestamp = has(created) ? { label: 'Created Date', value: created } : null;

  return { eyebrow, title, status, ids, meta, timestamp };
}

export function getModuleConfig(moduleName) {
  const registry = getEffectiveRegistry();
  return registry[resolveModuleKey(moduleName, registry)] ?? null;
}

export function buildHeaderFromGroups(moduleName, formGroups = []) {
  if (!formGroups.length) return null;

  const registry = getEffectiveRegistry();
  const config = registry[resolveModuleKey(moduleName, registry)];

  const fields = indexFields(formGroups);
  const val = (key) => {
    const field = fields.get(key);
    if (!field) {
      // Audit stamps aren't form fields — getFormGroups rides them along as
      // recordMeta on the groups array so headers can show "Created on …".
      const meta = formGroups.recordMeta;
      if (meta && (key === 'createdAt' || key === 'updatedAt')) return meta[key];
      return undefined;
    }
    if (!isEmpty(field.value)) return field.value;

    // Status defaults are meaningful in headers when an older record has no
    // embedded status value. Other field defaults remain add-form concerns and
    // must not be presented as persisted detail data.
    if (['status', 'jobStatus', 'submissionStatus'].includes(key)
      && !isEmpty(field.defaultValue)) {
      return field.defaultValue;
    }
    return field.value;
  };

  // Generic defaults always run; a per-module header (when present) layers on
  // top so every module renders a complete, consistent header. A module may
  // describe its header declaratively via `header` (preferred, see headerSpec)
  // or imperatively via `buildData` — both produce the same shape.
  const generic = genericBuildData({ val, has });
  const specific = config?.buildData
    ? (config.buildData({ val, has }) ?? {})
    : (config?.header ? buildFromSpec(config.header, { val, has }) : {});

  // An explicitly configured array owns the section even when it is empty.
  // This lets a host config intentionally suppress generic ids/meta by
  // returning `ids: []` or `meta: []`, while an omitted property still falls
  // back to the generic builder.
  const preferArray = (specificValue, genericValue) => (
    Array.isArray(specificValue) ? specificValue : (genericValue ?? [])
  );

  return {
    eyebrow: specific.eyebrow ?? generic.eyebrow,
    title: specific.title ?? generic.title ?? '-',
    clientName: specific.clientName ?? null,
    clientId: specific.clientId ?? null,
    status: specific.status ?? generic.status ?? null,
    titleIds: preferArray(specific.titleIds, generic.titleIds),
    meta: preferArray(specific.meta, generic.meta),
    ids: preferArray(specific.ids, generic.ids),
    stats: specific.stats ?? [],
    timestamp: specific.timestamp ?? generic.timestamp ?? null,
    tabs: config?.tabs ?? [],
  };
}

export { isEmpty, statusColor };
